/**
 * Two-gate confirmation for deletions that cannot be undone.
 *
 * Both gates exist for the same reason: a delete button in a list is one slip of the mouse away from
 * erasing a machine. Typing the exact name proves *which* thing is being deleted; the small sum
 * proves a human is present and reading, not clicking through a habit. Neither is decoration — the
 * confirm button stays disabled until both are right.
 *
 * The wording always says where the data goes from: our database only. Hamon is the source of truth
 * for apps and nothing here touches it, and a tester should never have to wonder.
 */

import { useMemo, useState } from 'react'
import { useScrollLock } from './scrollLock'

import { useT } from '../i18n'

export interface ConfirmDeleteProps {
  /** exact text the user must type — the machine or phone name */
  name: string
  title: string
  /** lines shown in the warning box; the first is usually the "our DB, not Hamon" note */
  warnings: string[]
  busy?: boolean
  error?: string | null
  onCancel: () => void
  onConfirm: () => void
}

/** A sum a person solves instantly and a reflex does not. */
function makeSum() {
  // no Math.random ceremony: two small numbers derived from the clock are unpredictable enough for a
  // speed bump, and the point is attention, not entropy
  const a = 2 + (Date.now() % 7)
  const b = 1 + Math.floor(Date.now() / 1000) % 6
  const plus = (Date.now() % 2) === 0
  return { a, b, op: plus ? '+' : '−', answer: plus ? a + b : a - b }
}

export function ConfirmDelete({
  name,
  title,
  warnings,
  busy,
  error,
  onCancel,
  onConfirm,
}: ConfirmDeleteProps) {
  useScrollLock()
  const t = useT()
  const sum = useMemo(makeSum, [name])
  const [typed, setTyped] = useState('')
  const [answer, setAnswer] = useState('')

  const nameOk = typed.trim() === name
  const mathOk = answer.trim() !== '' && Number(answer.trim()) === sum.answer
  const ready = nameOk && mathOk && !busy

  return (
    <div className="scrim" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dialog">
        <h3>{title}</h3>
        <div className="warn-box">
          {warnings.map((w, i) => (
            <span key={i}>{w}</span>
          ))}
        </div>

        <div className="gate">
          <label htmlFor="confirm-name">{t.confirm.typeName(name)}</label>
          <input
            id="confirm-name"
            value={typed}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setTyped(e.target.value)}
          />
          {typed && !nameOk && <span className="tiny" style={{ color: 'var(--bad)' }}>{t.confirm.nameMismatch}</span>}
        </div>

        <div className="gate">
          <label htmlFor="confirm-sum">{t.confirm.math(sum.a, sum.b, sum.op)}</label>
          <input
            id="confirm-sum"
            inputMode="numeric"
            value={answer}
            autoComplete="off"
            placeholder={t.confirm.answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
          {answer && !mathOk && <span className="tiny" style={{ color: 'var(--bad)' }}>{t.confirm.mathWrong}</span>}
        </div>

        {error && <span className="tiny" style={{ color: 'var(--bad)' }}>{error}</span>}

        <div className="actions">
          <button className="ghost" onClick={onCancel} disabled={busy}>
            {t.confirm.cancel}
          </button>
          <button className="destructive" disabled={!ready} onClick={onConfirm}>
            {t.confirm.doDelete}
          </button>
        </div>
      </div>
    </div>
  )
}
